import React, { useState, useEffect, useContext } from 'react';
import { reportEvent } from '@com/core/common/utils/sentry';
import { catalogSVC } from '@com/core/common/services/api';
import { AppContext } from '@com/core/common/contexts/appContext';
import { THit } from '../types/catalog';
import { StoreContext } from '../contexts/storeContext';

type TProps = {
    productUrl?: string;
    limit?: number;
    productSku?: string;
    productSkus?: string[];
    children: (data: THit | THit[] | null, isLoading: boolean) => JSX.Element | null;
};

type TError = Error & {
    code: 'error' | 'critical' | 'warning' | 'info' | 'debug';
};

const DynamicWidgetComponent = ({
    children,
    limit,
    productSku,
    productSkus,
    productUrl,
}: TProps): JSX.Element | null => {
    const { locale } = useContext(AppContext);
    const { storeCode } = useContext(StoreContext);

    const [data, setData] = useState<THit[] | THit | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<TError | null>(null);

    const successHandler = (result: THit | THit[]): void => {
        setIsLoading(false);
        setData(result);
    };

    const errorHandler = (e: TError, info: string): void => {
        setIsLoading(false);
        setError(e);

        reportEvent(
            "Couldn't fetch dynamic widget data",
            `homePage.DynamicWidgetComponent.componentDidMount.fetchWidgetData.${info}`,
            e.code,
            e,
        );
    };

    const catalogPrefix = storeCode ? '' : '/c';

    useEffect(() => {
        if (productSku) {
            catalogSVC(locale.apiLocale)
                .getProductDetailsForSkus(productSku)
                .then(({ data: { hits } }): void => successHandler(hits[0]))
                .catch((e: TError): void => errorHandler(e, 'DynamicWidget.fetchWidgetData.productSku'));
        } else if (productSkus) {
            catalogSVC(locale.apiLocale)
                .getProductDetailsForSkus(productSkus.join(','))
                .then(({ data: { hits } }) => successHandler(hits))
                .catch((e: TError) => errorHandler(e, 'DynamicWidget.fetchWidgetData.productSkus'));
        } else if (productUrl) {
            // Will get maximum 18 products if limit not passed through props.
            catalogSVC(locale.apiLocale)
                .getCatalogData(`${catalogPrefix}${productUrl}`, { params: { limit: limit || 18 } })
                .then(({ data: { hits } }) => successHandler(hits))
                .catch((e: TError) => errorHandler(e, 'DynamicWidget.fetchWidgetData.productUrl'));
        }
    }, [locale, limit, productSku, productSkus, productUrl]);

    /* Show errors in staging, or hide in production */
    if (error) {
        if (process.env.ENV === 'staging' || process.env.ENV === 'dev') {
            return <div style={{ color: 'red', border: 'solid 1px red' }}>{JSON.stringify(error)}</div>;
        }
        return null;
    }

    // Function should be passed as children
    return children(data, isLoading);
};

export default DynamicWidgetComponent;
